// src/StaffAssignments.js
import React, { useEffect, useState, useMemo } from "react";
import { fetchWithAuth } from "../config/api";

export default function StaffAssignments() {
  const [loading, setLoading] = useState(true);
  const [staff, setStaff] = useState([]);
  const [catalogs, setCatalogs] = useState([]);
  const [services, setServices] = useState([]);
  const [assignments, setAssignments] = useState({}); // serviceId -> Set(staffId)
  const [selectedCatalogId, setSelectedCatalogId] = useState("");

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetchWithAuth("/api/staff/service-assignments/");
        const data = await res.json();
        setStaff(data.staff || []);
        setCatalogs(data.catalogs || []);
        setServices(data.services || []);

        const map = {};
        (data.assignments || []).forEach((a) => {
          map[a.service_id] = new Set(a.staff_ids || []);
        });
        setAssignments(map);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const servicesForCatalog = useMemo(() => {
    if (!selectedCatalogId) return [];
    const cid = Number(selectedCatalogId);
    return services.filter((s) => s.catalog_id === cid);
  }, [services, selectedCatalogId]);

  const toggleAssignment = (serviceId, staffId) => {
    setAssignments((prev) => {
      const next = { ...prev };
      const current = new Set(next[serviceId] || []);
      if (current.has(staffId)) {
        current.delete(staffId);
      } else {
        current.add(staffId);
      }
      next[serviceId] = current;
      return next;
    });
  };

  const save = async () => {
    const payload = {
      assignments: Object.entries(assignments).map(([serviceId, staffSet]) => ({
        service_id: Number(serviceId),
        staff_ids: Array.from(staffSet),
      })),
    };
    const res = await fetchWithAuth("/api/staff/service-assignments/save/", {
      method: "POST",
      body: JSON.stringify(payload),
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      alert("Failed to save assignments");
      return;
    }
    alert("Assignments saved!");
  };

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", padding: "2rem" }}>
        <div className="card" style={{ maxWidth: 600, margin: "2rem auto" }}>
          <h2>Loading staff assignments…</h2>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", padding: "2rem" }}>
      <div className="card" style={{ maxWidth: 1000, margin: "0 auto" }}>
        <h2>Staff Service Assignments</h2>

        <div style={{ marginBottom: 16 }}>
          <label className="label">Catalog</label>
          <select
            className="input"
            value={selectedCatalogId}
            onChange={(e) => setSelectedCatalogId(e.target.value)}
          >
            <option value="">Select catalog…</option>
            {catalogs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {!selectedCatalogId && <p>Select a catalog to manage assignments.</p>}

        {selectedCatalogId && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: 8 }}>Service</th>
                  {staff.map((s) => (
                    <th key={s.id} style={{ padding: 8 }}>{s.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {servicesForCatalog.map((svc) => (
                  <tr key={svc.id}>
                    <td style={{ padding: 8 }}>{svc.name}</td>
                    {staff.map((s) => {
                      const selected =
                        assignments[svc.id] &&
                        assignments[svc.id].has(s.id);
                      return (
                        <td key={s.id} style={{ textAlign: "center", padding: 4 }}>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleAssignment(svc.id, s.id)}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <button className="btn" onClick={save}>
            Save Assignments
          </button>
        </div>
      </div>
    </div>
  );
}
